// Enhanced Market translation functions for IDX Broker to iHomeFinder conversion
// Refactored for better maintainability and reduced redundancy

import { MarketResource, NewMarketResource } from "api/resource/market";
import { CriterionResource } from "api/resource/criterion";
import { getCities } from "redux/selector";
import { RootState } from "redux/store";
import { City } from "model/city";
import { getIDXBrokerCityName } from './idx-broker-cities';
import { PropertyType } from "type/property-type";

export interface TranslationResult<T = any> {
	translated: T[];
	skipped: any[];
	errors: string[];
	warnings: string[];
	cityTranslations?: CityTranslationResult[];
}

export interface CityTranslationResult {
	idxBrokerId: string;
	idxBrokerName?: string;
	iHomefinderCity?: City;
	success: boolean;
	error?: string;
}

export interface ConditionalValueMapping {
	idxFieldName: string;
	valueMappings: {
		[idxValue: string]: {
			iHomefinderField: string;
			iHomefinderValue: boolean | string | number;
			label?: string;
		};
	};
}

// CENTRALIZED CONFIGURATION
// ========================

// Single source of truth for ignored fields (including meta fields)
export const IDX_BROKER_IGNORED_FIELDS: Set<string> = new Set([
	"ccz",
	"idxID", 
	"per",
	"srt",
	"page",
	"a_propSubType",
	"searchRefinement[]",
	"subdivision"
]);

// Meta fields are now just ignored - we don't want them in market criteria
// If you need to track these values separately, handle them outside the criteria conversion

// Boolean field indicators
export const BOOLEAN_FIELD_SUFFIXES = ['Yn', 'yn'];

// Numeric fields that need conversion
export const NUMERIC_FIELDS = new Set([
	'bedrooms', 'bathCount', 'minListPrice', 'maxListPrice', 
	'maxSquareFeet', 'lotAcres', 'minPhotoCount', 'resultsPerPage'
]);

// Comprehensive field definition interface
interface FieldDefinition {
	iHomefinderField: string | string[];
	label?: string;
	isArea?: boolean;
	isNumeric?: boolean;
	isBoolean?: boolean;
}

// Combined field mapping with metadata
export const IDX_BROKER_FIELD_DEFINITIONS: Record<string, FieldDefinition> = {
	'add': { 
		iHomefinderField: 'fullPartialAddress',
		label: 'Address'
	},
	'ccz': { 
		iHomefinderField: ['city', 'county', 'zip'],
		isArea: true
	},
	'city[]': { 
		iHomefinderField: 'cityId',
		label: 'City',
		isArea: true
	},
	'county[]': { 
		iHomefinderField: 'county',
		label: 'County',
		isArea: true
	},
	'pgon': { 
		iHomefinderField: 'boundaryWKT',
		label: 'Boundary'
	},
	'zipcode[]': { 
		iHomefinderField: 'zip',
		label: 'Postal Code',
		isArea: true
	},
	'a_architecturalstyle[]': { 
		iHomefinderField: 'style',
		label: 'Style'
	},
	'a_newconstructionyn': { 
		iHomefinderField: 'newConstructionYn',
		label: 'New Construction',
		isBoolean: true
	},
	'a_propStatus[]': { 
		iHomefinderField: 'status',
		label: 'Status'
	},
	'a_propSubType[]': { 
		iHomefinderField: 'propertySubType',
		label: 'Property Subtype'
	},
	'a_speciallistingconditions[]': { 
		iHomefinderField: ['distressedYn', 'shortSaleYn'],
		label: 'Special Conditions'
	},
	'a_subdivision[]': { 
		iHomefinderField: 'subdivision',
		label: 'Subdivision'
	},
	'amin_acres': { 
		iHomefinderField: 'lotAcres',
		label: 'Min. Lot Acres',
		isNumeric: true
	},
	'amax_sqFt': { 
		iHomefinderField: 'maxSquareFeet',
		label: 'Max. SqFt.',
		isNumeric: true
	},
	'bd': { 
		iHomefinderField: 'bedrooms',
		label: 'Min. Bedrooms',
		isNumeric: true
	},
	'fl': { 
		iHomefinderField: 'foreclosureYn',
		label: 'Foreclosure',
		isBoolean: true
	},
	'idxID': { 
		iHomefinderField: 'listingNumber',
		label: 'Listing Number'
	},
	'pt': { 
		iHomefinderField: 'propertyType',
		label: 'Property Type'
	},
	'tb': { 
		iHomefinderField: 'bathCount',
		label: 'Min. Bathrooms',
		isNumeric: true
	},
	'a_poolprivateyn': { 
		iHomefinderField: 'poolYn',
		label: 'Pool',
		isBoolean: true
	},
	'a_view[]': { 
		iHomefinderField: 'viewDescription',
		label: 'View Description'
	},
	'a_waterfrontyn': { 
		iHomefinderField: 'waterFrontYn',
		label: 'Waterfront',
		isBoolean: true
	},
	'hp': { 
		iHomefinderField: 'maxListPrice',
		label: 'Max. Price',
		isNumeric: true
	},
	'lp': { 
		iHomefinderField: 'minListPrice',
		label: 'Min. Price',
		isNumeric: true
	},
	'a_elementaryschool[]': { 
		iHomefinderField: 'elementarySch',
		label: 'Elementary School'
	},
	'a_highschool[]': { 
		iHomefinderField: 'highSch',
		label: 'High School'
	},
	'a_middleorjuniorschool[]': { 
		iHomefinderField: 'middleSch',
		label: 'Middle School'
	},
	'amin_photocount': { 
		iHomefinderField: 'minPhotoCount',
		label: 'Minimum Photo Count',
		isNumeric: true
	},
	'aw_address': { 
		iHomefinderField: 'fullPartialAddress',
		label: 'Address'
	},
	'woh': { 
		iHomefinderField: 'openHomesOnlyYn',
		label: 'Open Houses',
		isBoolean: true
	},
	'wvoh': { 
		iHomefinderField: 'virtualTourYn',
		label: 'Virtual Tour',
		isBoolean: true
	},
	'idxStatus': { 
		iHomefinderField: 'status',
		label: 'Status'
	}
};

// Conditional value mappings for fields that need value-based branching
export const CONDITIONAL_VALUE_MAPPINGS: ConditionalValueMapping[] = [
	{
		idxFieldName: 'a_associationamenities[]',
		valueMappings: {
			'Golf Course': { 
				iHomefinderField: 'golfCourseYn', 
				iHomefinderValue: true,
				label: 'Golf Course'
			},
			'Golf+Course': { 
				iHomefinderField: 'golfCourseYn', 
				iHomefinderValue: true,
				label: 'Golf Course'
			},
			'Pool': { 
				iHomefinderField: 'poolYn', 
				iHomefinderValue: true,
				label: 'Pool'
			}
		}
	},
	{
		idxFieldName: 'aw_remarksConcat',
		valueMappings: {
			'equestrian': { 
				iHomefinderField: 'horsePropertyYn', 
				iHomefinderValue: true,
				label: 'Horse Property'
			}
		}
	},
	{
		idxFieldName: 'pt',
		valueMappings: {
			'1': { iHomefinderField: 'propertyType', iHomefinderValue: PropertyType.RESIDENTIAL.id },
			'2': { iHomefinderField: 'propertyType', iHomefinderValue: PropertyType.RENTAL.id },
			'3': { iHomefinderField: 'propertyType', iHomefinderValue: PropertyType.MULTI_UNIT.id },
			'4': { iHomefinderField: 'propertyType', iHomefinderValue: PropertyType.HOUSE.id },
			'7': { iHomefinderField: 'propertyType', iHomefinderValue: PropertyType.LOTS_LAND.id },
			'8': { iHomefinderField: 'propertyType', iHomefinderValue: PropertyType.COMMERCIAL.id },
			'9': { iHomefinderField: 'propertyType', iHomefinderValue: PropertyType.COMMERCIAL.id }
		}
	}
];

// UTILITY FUNCTIONS
// ================

// Centralized value normalization
export const normalizeValue = (value: string): string => {
	return decodeURIComponent(value.replace(/\+/g, ' '));
};

// Centralized value conversion using field definitions
export const convertValue = (value: string, fieldDefinition: FieldDefinition): boolean | string | number => {
	// Boolean conversion
	if (fieldDefinition.isBoolean) {
		const normalized = value.toLowerCase();
		return normalized === 'y' || normalized === 'yes' || normalized === 'true' || normalized === '1';
	}
	
	// Numeric conversion
	if (fieldDefinition.isNumeric) {
		const parsed = parseFloat(value);
		return isNaN(parsed) ? value : parsed;
	}
	
	return value;
};

export const generateFieldLabel = (fieldName: string, fieldDefinition?: FieldDefinition, valueMapping?: ConditionalValueMapping['valueMappings'][string]): string => {
	// Use the label from field definition if available
	if (fieldDefinition?.label) {
		return fieldDefinition.label;
	}
	
	// Use label from conditional value mapping if available
	if (valueMapping?.label) {
		return valueMapping.label;
	}
	
	// Generate from field name as last resort
	return fieldName.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
};

// SPECIALIZED PROCESSORS
// =====================

// Helper function to find conditional mapping for a field
const findConditionalMapping = (fieldName: string): ConditionalValueMapping | undefined => {
	return CONDITIONAL_VALUE_MAPPINGS.find(mapping => mapping.idxFieldName === fieldName);
};

// Helper function to add or update criterion
const addOrUpdateCriterion = (
	criteria: CriterionResource[], 
	valueMapping: ConditionalValueMapping['valueMappings'][string]
): void => {
	const existingIndex = criteria.findIndex(c => c.name === valueMapping.iHomefinderField);
	
	if (existingIndex === -1) {
		criteria.push({
			name: valueMapping.iHomefinderField,
			label: generateFieldLabel(valueMapping.iHomefinderField, undefined, valueMapping),
			value: valueMapping.iHomefinderValue
		});
	} else if (typeof valueMapping.iHomefinderValue === 'boolean' && valueMapping.iHomefinderValue) {
		// Keep boolean true values
		criteria[existingIndex].value = true;
	}
};

// Process conditional value mappings
export const processConditionalMapping = (
	fieldName: string, 
	values: string[], 
	criteria: CriterionResource[]
): { processed: boolean; warnings: string[] } => {
	const mapping = findConditionalMapping(fieldName);
	const warnings: string[] = [];
	
	if (!mapping) {
		return { processed: false, warnings };
	}
	
	values.forEach(value => {
		const normalizedValue = normalizeValue(value);
		const valueMapping = mapping.valueMappings[value] || mapping.valueMappings[normalizedValue];
		
		if (valueMapping) {
			addOrUpdateCriterion(criteria, valueMapping);
		} else {
			warnings.push(`No mapping found for ${fieldName} value: "${value}"`);
		}
	});
	
	return { processed: true, warnings };
};

// City translation functions
export const findIHomefinderCityByName = (cities: City[], cityName: string): City | undefined => {
	const normalizedSearchName = cityName.toLowerCase().trim();
	
	// Exact match first
	let match = cities.find(city => city.name.toLowerCase() === normalizedSearchName);
	if (match) return match;
	
	// Partial match (starts with)
	match = cities.find(city => city.name.toLowerCase().startsWith(normalizedSearchName));
	if (match) return match;
	
	// Contains match
	match = cities.find(city => city.name.toLowerCase().includes(normalizedSearchName));
	return match;
};

export const translateCities = (idxCityIds: string[], cities: City[]): CityTranslationResult[] => {
	return idxCityIds.map(idxId => {
		const idxCityName = getIDXBrokerCityName(idxId);
	
		if (!idxCityName) {
			return {
				idxBrokerId: idxId,
				success: false,
				error: `IDX Broker city ID ${idxId} not found in city database`
			};
		}
		
		const iHomefinderCity = findIHomefinderCityByName(cities, idxCityName);
		
		if (!iHomefinderCity) {
			return {
				idxBrokerId: idxId,
				idxBrokerName: idxCityName,
				success: false,
				error: `iHomeFinder city not found for "${idxCityName}"`
			};
		}
		
		return {
			idxBrokerId: idxId,
			idxBrokerName: idxCityName,
			iHomefinderCity,
			success: true
		};
	});
};

// MAIN CONVERSION FUNCTIONS
// ========================

export const parseQueryString = (queryString: string): Record<string, string | string[]> => {
	const params: Record<string, string | string[]> = {};
	
	if (!queryString) return params;
	
	const cleanQuery = queryString.startsWith('?') ? queryString.slice(1) : queryString;
	
	cleanQuery.split('&').forEach(param => {
		const [key, value] = param.split('=').map(decodeURIComponent);
		
		if (!key || value === undefined) return;
		
		if (key.endsWith('[]')) {
			if (!params[key]) params[key] = [];
			(params[key] as string[]).push(value);
		} else {
			if (params[key]) {
				if (!Array.isArray(params[key])) {
					params[key] = [params[key] as string];
				}
				(params[key] as string[]).push(value);
			} else {
				params[key] = value;
			}
		}
	});
	
	return params;
};

export const convertToCriteria = (
	params: Record<string, string | string[]>, 
): { criteria: CriterionResource[]; warnings: string[] } => {
	const criteria: CriterionResource[] = [];
	const warnings: string[] = [];
	
	Object.entries(params).forEach(([key, value]) => {
		// Skip ignored fields (including meta fields)
		if (IDX_BROKER_IGNORED_FIELDS.has(key)) {
			return;
		}
		
		// Try conditional mapping first
		const values = Array.isArray(value) ? value : [value];
		const conditionalResult = processConditionalMapping(key, values, criteria);
		
		if (conditionalResult.processed) {
			warnings.push(...conditionalResult.warnings);
			return;
		}
		
		// Handle regular field mapping
		processRegularField(key, value, criteria, warnings);
	});
	
	return { criteria, warnings };
};

// Helper function for regular field processing
function processRegularField(
	key: string,
	value: string | string[],
	criteria: CriterionResource[],
	warnings: string[],
): void {
	const fieldDefinition = IDX_BROKER_FIELD_DEFINITIONS[key];
	
	if (!fieldDefinition) {
		criteria.push({
			name: key,
			label: generateFieldLabel(key),
			value: value
		});
		warnings.push(`No mapping found for field: ${key}`);
		return;
	}
	
	const mappedField = fieldDefinition.iHomefinderField;
	
	// Handle array mappings (one IDX field maps to multiple iHomeFinder fields)
	if (Array.isArray(mappedField)) {
		mappedField.forEach(field => {
			criteria.push({
				name: field,
				label: generateFieldLabel(field, fieldDefinition),
				value: Array.isArray(value) ? value : [value],
				area: fieldDefinition.isArea || false
			});
		});
	} else {
		// Handle single field mapping
		const processedValue = typeof value === 'string' 
			? convertValue(value, fieldDefinition)
			: value;
		
		criteria.push({
			name: mappedField,
			label: generateFieldLabel(mappedField, fieldDefinition),
			value: processedValue,
			area: fieldDefinition.isArea || false
		});
	}
}

// MAIN TRANSLATION FUNCTION
// ========================

export const translateToMarket = (sourceData: any[], state?: RootState): TranslationResult<NewMarketResource> => {
	const result: TranslationResult<NewMarketResource> = {
		translated: [],
		skipped: [],
		errors: [],
		warnings: []
	};

sourceData.forEach((marketData, index) => {
	try {
		if (!marketData.id || !marketData.linkTitle || !marketData.queryString) {
			result.errors.push(`Market ${index + 1}: Missing required fields (id, linkTitle, queryString)`);
			result.skipped.push(marketData);
			return;
		}
		
		const parsedParams = parseQueryString(marketData.queryString);
		const { criteria, warnings } = convertToCriteria(parsedParams);
		
		warnings.forEach(warning => {
			result.warnings.push(`Market ${index + 1}: ${warning}`);
		});
		
		const cleanTitle = marketData.linkTitle.replace(/[\r\n]+/g, " ").trim();

		const marketResource: NewMarketResource = {
			name: cleanTitle,
			description: cleanTitle,
			criteria,
			showOnIndex: marketData.featured === 'y',
			sortOrder: 1,
			inactive: false,
		};
			
		result.translated.push(marketResource);
		
	} catch(error) {
		result.errors.push(`Market ${index + 1}: ${error instanceof Error ? error.message : 'Unknown translation error'}`);
		result.skipped.push(marketData);
	}
});

return result;
};

// VALIDATION FUNCTIONS
// ===================

export const validateMarketResource = (marketResource: NewMarketResource): string[] => {
	const errors: string[] = [];
		
	if (!marketResource.name || typeof marketResource.name !== 'string') {
		errors.push('name is required and must be a string');
	}
	
	if (typeof marketResource.inactive !== 'boolean') {
		errors.push('inactive must be a boolean');
	}
	
	if (marketResource.criteria) {
		if (!Array.isArray(marketResource.criteria)) {
			errors.push('criteria must be an array');
		} else {
			marketResource.criteria.forEach((criterion, index) => {
				const criterionErrors = validateCriterion(criterion, index);
				errors.push(...criterionErrors);
			});
		}
	}
	
	// Validate optional fields
	errors.push(...validateOptionalFields(marketResource));
	
	return errors;
};

function validateCriterion(criterion: CriterionResource, index: number): string[] {
	const errors: string[] = [];
	
	if (!criterion.name || typeof criterion.name !== 'string') {
		errors.push(`criteria[${index}]: name is required and must be a string`);
	}
	
	if (!criterion.label || typeof criterion.label !== 'string') {
		errors.push(`criteria[${index}]: label is required and must be a string`);
	}
	
	if (criterion.value === undefined || criterion.value === null) {
		errors.push(`criteria[${index}]: value is required`);
	} else {
		const validTypes = ['boolean', 'string', 'number'];
		const valueType = typeof criterion.value;
		const isArray = Array.isArray(criterion.value);
		
		if (!isArray && !validTypes.includes(valueType)) {
			errors.push(`criteria[${index}]: value must be boolean, string, number, or array`);
		}
		
		if (isArray && !(criterion.value as any[]).every(v => validTypes.includes(typeof v))) {
			errors.push(`criteria[${index}]: array value must contain only strings, numbers, or booleans`);
		}
	}
	
	if (criterion.area !== undefined && typeof criterion.area !== 'boolean') {
		errors.push(`criteria[${index}]: area must be a boolean if provided`);
	}
	
	return errors;
}

function validateOptionalFields(marketResource: NewMarketResource): string[] {
	const errors: string[] = [];
	
	if (marketResource.sortOrder !== undefined && typeof marketResource.sortOrder !== 'number') {
		errors.push('sortOrder must be a number if provided');
	}
	
	const stringFields = ['description', 'header', 'footer', 'subhead', 'photoUrl'];
	stringFields.forEach(field => {
		const value = (marketResource as any)[field];
		if (value !== undefined && typeof value !== 'string') {
			errors.push(`${field} must be a string if provided`);
		}
	});
	
	return errors;
}

// UTILITY EXPORTS
// ===============

export const getAvailableConditionalMappings = (): { field: string; values: string[] }[] => {
	return CONDITIONAL_VALUE_MAPPINGS.map(mapping => ({
		field: mapping.idxFieldName,
		values: Object.keys(mapping.valueMappings)
	}));
};

// Legacy compatibility functions
export const translateToLead = (sourceData: any[]): TranslationResult<any> => {
	return {
		translated: [],
		skipped: sourceData,
		errors: ["Lead translation - Coming Soon"],
		warnings: []
	};
};

export const translateToSavedSearch = (sourceData: any[]): TranslationResult<any> => {
	return {
		translated: [],
		skipped: sourceData,
		errors: ["SavedSearch translation - Coming Soon"],
		warnings: []
	};
};

export const getAvailableTranslators = (): string[] => {
	return ["Market"];
};